
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { EmployeeList } from '@/components/employees/EmployeeList';
import { Employee } from '@/types';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { useAuth } from '@/contexts/AuthContext';

const Employees = () => {
  const { data: contextData, loading: contextLoading, refresh } = useAppDataContext();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const handleAddEmployee = async (employee: Partial<Employee>) => {
    try {
      // Create a complete employee object
      const newEmployee: Employee = {
        id: `emp-${Date.now().toString(36)}`,
        userId: `user-${Date.now().toString(36)}`,
        employeeId: `EMP${Math.floor(10000 + Math.random() * 90000)}`,
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        otherName: employee.otherName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        cellNumber: employee.cellNumber || '',
        position_id: employee.position_id ? Number(employee.position_id) : undefined,
        department_id: employee.department_id ? Number(employee.department_id) : undefined,
        division: employee.division || '',
        joinDate: employee.engagementDate || '',
        engagementDate: employee.engagementDate || '',
        dateOfBirth: employee.dateOfBirth || '',
        gender: employee.gender || 'male',
        socialSecurityNo: employee.socialSecurityNo || '',
        status: employee.status || 'active',
        basicSalary: employee.basicSalary || 0,
        bankName: employee.bankName || '',
        bankAccount: employee.bankAccount || '',
        bankBranch: employee.bankBranch || '',
        manNumber: employee.manNumber || '',
        nrc: employee.nrc || '',
        jobTitle: employee.jobTitle || '',
        taxId: employee.taxId || '',
        nhimaNumber: employee.nhimaNumber || '',
        unionType: employee.unionType || '',
        contributionType: employee.contributionType || '',
        cid: currentUser?.cid || null
      };

      // Create employee via API
      await apiService.createEmployee(newEmployee);
      
      await refresh.employees();
      
      toast({
        title: 'Success',
        description: 'Employee added successfully',
      });
    } catch (error) {
      console.error('Error adding employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to add employee',
        variant: 'destructive',
      });
    }
  };

  const handleEditEmployee = async (id: string, updatedEmployee: Partial<Employee>) => {
    try {
      // Update employee via API - id is included in the payload, not URL
      await apiService.updateEmployee(id, updatedEmployee);
      
      // Refresh employee list from context
      await refresh.employees();
      
      toast({
        title: 'Success',
        description: 'Employee updated successfully',
      });
    } catch (error) {
      console.error('Error updating employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to update employee',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      // Delete employee via API
      await apiService.deleteEmployee(id);
      
      // Refresh employee list from context
      await refresh.employees();
      
      toast({
        title: 'Success',
        description: 'Employee deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete employee',
        variant: 'destructive',
      });
    }
  };

  if (contextLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-semibold mb-6">Employees</h1>
        <EmployeeList 
          employees={contextData.employees}
          onAddEmployee={handleAddEmployee}
          onEditEmployee={handleEditEmployee}
          onDeleteEmployee={handleDeleteEmployee}
          onImportComplete={() => refresh.employees()}
        />
      </div>
    </DashboardLayout>
  );
};

export default Employees;
